/**
 * Hand-drawn canvas charts of the Mockup logs dashboard. Colors of text, grid and surfaces are read from
 * the CSS custom properties the dashboard wrapper sets from the design tokens (`palette.ts`), so every
 * chart follows the light / dark theme; series colors come from the caller.
 */

export interface ISlice {
  label?: string;
  value: number;
  color: string;
  /** Text color of the percent label drawn on the slice (`tone.*.contrast`). */
  labelColor?: string;
}

export interface ILinePoint {
  x: string;
  y: number;
}

export interface ILineSeries {
  name: string;
  color: string;
  data: ILinePoint[];
  dashed?: boolean;
  fill?: string;
}

export interface IDonutOpts {
  donut?: boolean;
  centerLabel?: string;
  centerSub?: string;
}

export interface IExtraTooltipRow {
  label: string;
  color?: string;
  /** Pre-formatted string value per data-point index */
  values: string[];
}

export interface ILineOpts {
  yFmt?: (v: number) => string;
  startAtZero?: boolean;
  /** Additional calculated rows shown below a divider in the tooltip */
  extraRows?: IExtraTooltipRow[];
}

export interface IStackLayer {
  key: string;
  color: string;
  data: number[];
}

export interface IStackGroup {
  label?: string;
  stacks: IStackLayer[];
}

export interface IColumnLayer {
  key: string;
  color: string;
  data: number[];
}

export interface IColumnsOpts {
  yFmt?: (v: number) => string;
  /** Fixed axis maximum (100 for shares); computed from the data when omitted. */
  maxValue?: number;
  /** Previous period, drawn as a lighter column beside the current one. */
  prev?: IColumnLayer[] | null;
  /** Additional calculated rows shown below a divider in the tooltip */
  extraRows?: IExtraTooltipRow[];
}

interface ChartColors {
  text: string;
  textMuted: string;
  textDim: string;
  border: string;
  /** Text on top of a colored surface (the tooltip text color). */
  onColor: string;
  font: string;
}

const FALLBACK_FONT = '-apple-system, system-ui, sans-serif';

function dpr(): number {
  return typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
}

function setupCanvas(canvas: HTMLCanvasElement) {
  const r = canvas.getBoundingClientRect();
  canvas.width = Math.max(1, r.width * dpr());
  canvas.height = Math.max(1, r.height * dpr());
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context');
  ctx.setTransform(dpr(), 0, 0, dpr(), 0, 0);
  return { ctx, w: r.width, h: r.height };
}

/** Theme colors inherited from the dashboard wrapper's CSS variables (fallbacks only matter outside it). */
function chartColors(canvas: HTMLCanvasElement): ChartColors {
  const cs = getComputedStyle(canvas);
  const v = (name: string, fallback: string) => (cs.getPropertyValue(name) || '').trim() || fallback;
  return {
    text: v('--text', 'currentColor'),
    textMuted: v('--text-muted', 'currentColor'),
    textDim: v('--text-dim', 'currentColor'),
    border: v('--border', 'currentColor'),
    onColor: v('--tooltip-text', 'currentColor'),
    font: v('--font', FALLBACK_FONT),
  };
}

const esc = (value: unknown): string =>
  String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

/** Mix hex color with white: t=0 original, t=1 white (inner compare ring). */
function lightenHex(hex: string, t: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return hex;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  const lr = Math.round(r + (255 - r) * t);
  const lg = Math.round(g + (255 - g) * t);
  const lb = Math.round(b + (255 - b) * t);
  return `#${[lr, lg, lb].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

function cutHole(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number) {
  // A transparent hole instead of a painted disc, so the donut sits on any card background.
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function centerText(ctx: CanvasRenderingContext2D, colors: ChartColors, cx: number, cy: number, label?: string, sub?: string, size = 20) {
  if (!label) return;
  ctx.fillStyle = colors.text;
  ctx.font = `700 ${size}px ${colors.font}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, cx, cy - 8);
  if (sub) {
    ctx.fillStyle = colors.textMuted;
    ctx.font = `500 11px ${colors.font}`;
    ctx.fillText(sub, cx, cy + 12);
  }
}

export function drawDonut(canvas: HTMLCanvasElement, slices: ISlice[], opts: IDonutOpts = {}) {
  const { ctx, w, h } = setupCanvas(canvas);
  const colors = chartColors(canvas);
  ctx.clearRect(0, 0, w, h);
  const cx = w / 2;
  const cy = h / 2;
  const radius = Math.min(w, h) / 2 - 12;
  const inner = opts.donut ? radius * 0.58 : 0;
  const total = slices.reduce((s, x) => s + (x.value || 0), 0) || 1;
  let a = -Math.PI / 2;
  slices.forEach((s) => {
    const ang = ((s.value || 0) / total) * Math.PI * 2;
    if (ang <= 0) return;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, radius, a, a + ang);
    ctx.closePath();
    ctx.fillStyle = s.color;
    ctx.fill();
    if (s.value / total > 0.05) {
      const mid = a + ang / 2;
      const lx = cx + Math.cos(mid) * (radius * (inner ? 0.79 : 0.72));
      const ly = cy + Math.sin(mid) * (radius * (inner ? 0.79 : 0.72));
      ctx.fillStyle = s.labelColor || colors.onColor;
      ctx.font = `600 12px ${colors.font}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(((s.value / total) * 100).toFixed(1) + '%', lx, ly);
    }
    a += ang;
  });
  if (inner > 0) {
    cutHole(ctx, cx, cy, inner);
    centerText(ctx, colors, cx, cy, opts.centerLabel, opts.centerSub, 20);
  }
}

export function drawDonutCompare(
  canvas: HTMLCanvasElement,
  current: ISlice[],
  previous: { value: number }[] | null,
  opts: IDonutOpts = {}
) {
  const { ctx, w, h } = setupCanvas(canvas);
  const colors = chartColors(canvas);
  ctx.clearRect(0, 0, w, h);
  const cx = w / 2;
  const cy = h / 2;
  const outer = Math.min(w, h) / 2 - 12;
  const ringGap = 6;
  const ringW = (outer * 0.42) / 2;
  function drawRing(slices: ISlice[], rOuter: number, rInner: number) {
    const total = slices.reduce((s, x) => s + (x.value || 0), 0) || 1;
    let a = -Math.PI / 2;
    slices.forEach((s) => {
      const ang = ((s.value || 0) / total) * Math.PI * 2;
      if (ang <= 0) return;
      ctx.beginPath();
      ctx.arc(cx, cy, rOuter, a, a + ang);
      ctx.arc(cx, cy, rInner, a + ang, a, true);
      ctx.closePath();
      ctx.fillStyle = s.color;
      ctx.fill();
      a += ang;
    });
  }
  drawRing(current, outer, outer - ringW * 1.4);
  if (previous && previous.length) {
    const r2outer = outer - ringW * 1.4 - ringGap;
    const r2inner = r2outer - ringW;
    const innerPrev: ISlice[] = current.map((c, i) => ({
      label: c.label,
      value: previous[i]?.value ?? 0,
      color: lightenHex(c.color, 0.52),
    }));
    drawRing(innerPrev, r2outer, r2inner);
  }
  centerText(ctx, colors, cx, cy, opts.centerLabel, opts.centerSub, 18);
}

/** Catmull-Rom spline: smooth line through all points without overshoot. */
function buildSmoothPath(ctx: CanvasRenderingContext2D, pts: { x: number; y: number }[], tension = 0.18) {
  if (pts.length === 0) return;
  if (pts.length === 1) {
    ctx.moveTo(pts[0].x, pts[0].y);
    return;
  }
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;
    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
  }
}

interface HoverMeta {
  /** Data index under the cursor's x, or null when outside the plot. */
  indexAt: (x: number) => number | null;
  /** Horizontal anchor of a data index, in canvas pixels. */
  anchorX: (idx: number) => number;
  html: (idx: number) => string;
}

function tipRow(color: string, name: string, value: string, extra = ''): string {
  return `<div class="tip-row"><span class="tip-dot" style="background:${esc(color)};${extra}"></span><span class="tip-name">${esc(name)}</span><span class="tip-val">${esc(value)}</span></div>`;
}

function extraRowsHtml(rows: IExtraTooltipRow[], idx: number): string {
  const lines = rows
    .map((row) => {
      const v = row.values[idx];
      if (v == null || v === '') return '';
      return `<div class="tip-row"><span class="tip-dot" style="background:${esc(row.color ?? 'currentColor')};border-radius:2px;"></span><span class="tip-name">${esc(row.label)}</span><span class="tip-val tip-pct">${esc(v)}</span></div>`;
    })
    .join('');
  return lines ? `<div class="tip-divider"></div>${lines}` : '';
}

function unbindHover(canvas: HTMLCanvasElement) {
  canvas.onmousemove = null;
  canvas.onmouseleave = null;
  const tip = canvas.parentElement?.querySelector('.chart-tip') as HTMLDivElement | null;
  if (tip) tip.style.display = 'none';
}

function bindHover(canvas: HTMLCanvasElement, meta: HoverMeta) {
  let tip = canvas.parentElement?.querySelector('.chart-tip') as HTMLDivElement | null;
  if (!tip && canvas.parentElement) {
    tip = document.createElement('div');
    tip.className = 'chart-tip';
    canvas.parentElement.appendChild(tip);
  }
  const onMove = (e: MouseEvent) => {
    if (!tip) return;
    const rect = canvas.getBoundingClientRect();
    const idx = meta.indexAt(e.clientX - rect.left);
    if (idx === null) {
      tip.style.display = 'none';
      return;
    }
    tip.style.display = 'block';
    tip.innerHTML = meta.html(idx);
    const tipH = tip.offsetHeight || 80;
    const mouseY = e.clientY - rect.top;
    const xPx = meta.anchorX(idx);
    tip.style.left = Math.max(8, Math.min(canvas.clientWidth - 200, xPx - 90)) + 'px';
    tip.style.top = (mouseY > tipH + 20 ? mouseY - tipH - 10 : mouseY + 14) + 'px';
  };
  const onLeave = () => {
    if (tip) tip.style.display = 'none';
  };
  canvas.onmousemove = onMove;
  canvas.onmouseleave = onLeave;
}

function drawGrid(ctx: CanvasRenderingContext2D, colors: ChartColors, w: number, padL: number, padR: number, padT: number, ch: number, min: number, max: number, yFmt?: (v: number) => string, ticks = 4) {
  ctx.strokeStyle = colors.border;
  ctx.fillStyle = colors.textMuted;
  ctx.font = `500 10px ${colors.font}`;
  ctx.lineWidth = 1;
  for (let i = 0; i <= ticks; i++) {
    const y = padT + (ch * i) / ticks;
    ctx.beginPath();
    ctx.moveTo(padL, y);
    ctx.lineTo(w - padR, y);
    ctx.stroke();
    const v = max - ((max - min) * i) / ticks;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(yFmt ? yFmt(v) : Math.round(v).toLocaleString(), padL - 8, y);
  }
}

function drawXLabels(ctx: CanvasRenderingContext2D, colors: ChartColors, labels: string[], xAt: (i: number) => number, y: number) {
  const stride = Math.max(1, Math.ceil(labels.length / 12));
  ctx.fillStyle = colors.textMuted;
  ctx.font = `500 10px ${colors.font}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  labels.forEach((lbl, i) => {
    if (i % stride !== 0 && i !== labels.length - 1) return;
    ctx.fillText(lbl, xAt(i), y);
  });
}

export function drawLine(canvas: HTMLCanvasElement, series: ILineSeries[], opts: ILineOpts = {}) {
  const { ctx, w, h } = setupCanvas(canvas);
  const colors = chartColors(canvas);
  ctx.clearRect(0, 0, w, h);
  const padL = 50;
  const padR = 18;
  const padT = 14;
  const padB = 32;
  const cw = w - padL - padR;
  const ch = h - padT - padB;
  const labels = series[0] ? series[0].data.map((d) => d.x) : [];
  let max = -Infinity;
  let min = Infinity;
  series.forEach((s) =>
    s.data.forEach((d) => {
      if (d.y > max) max = d.y;
      if (d.y < min) min = d.y;
    })
  );
  if (!Number.isFinite(max)) max = 1;
  if (!Number.isFinite(min)) min = 0;
  if (opts.startAtZero !== false) min = 0;
  if (max === min) max = min + 1;
  max = max + (max - min) * 0.12;
  const yRange = max - min;
  drawGrid(ctx, colors, w, padL, padR, padT, ch, min, max, opts.yFmt);
  const xAt = (i: number) => padL + cw * (labels.length === 1 ? 0.5 : i / (labels.length - 1));
  drawXLabels(ctx, colors, labels, xAt, h - padB + 8);
  series.forEach((s) => {
    const n = s.data.length;
    const pts = s.data.map((d, i) => ({
      x: padL + cw * (n === 1 ? 0.5 : i / (n - 1)),
      y: padT + ch - ((d.y - min) / yRange) * ch,
    }));
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.dashed ? 1.4 : 2.2;
    ctx.setLineDash(s.dashed ? [4, 4] : []);
    if (s.fill && pts.length) {
      ctx.beginPath();
      buildSmoothPath(ctx, pts);
      ctx.lineTo(pts[pts.length - 1].x, padT + ch);
      ctx.lineTo(pts[0].x, padT + ch);
      ctx.closePath();
      ctx.fillStyle = s.fill;
      ctx.fill();
    }
    ctx.beginPath();
    buildSmoothPath(ctx, pts);
    ctx.stroke();
    ctx.setLineDash([]);
  });
  const extraRows = opts.extraRows ?? [];
  bindHover(canvas, {
    indexAt: (x) => {
      if (labels.length === 0) return null;
      const ratio = Math.max(0, Math.min(1, (x - padL) / cw));
      return Math.round(ratio * (labels.length - 1));
    },
    anchorX: xAt,
    html: (idx) => {
      const lines = series
        .map((s) => {
          const d = s.data[idx];
          if (!d) return '';
          return tipRow(s.color, s.name, opts.yFmt ? opts.yFmt(d.y) : Number(d.y).toLocaleString(), s.dashed ? 'opacity:.55;' : '');
        })
        .join('');
      return `<div class="tip-title">${esc(labels[idx])}</div>${lines}${extraRowsHtml(extraRows, idx)}`;
    },
  });
}

/** Stacked columns per label (counts or shares); the previous period, if given, is a lighter column beside each. */
export function drawColumns(canvas: HTMLCanvasElement, labels: string[], layers: IColumnLayer[], opts: IColumnsOpts = {}) {
  const { ctx, w, h } = setupCanvas(canvas);
  const colors = chartColors(canvas);
  ctx.clearRect(0, 0, w, h);
  const n = labels.length;
  if (n === 0) {
    unbindHover(canvas);
    return;
  }
  const padL = 50;
  const padR = 14;
  const padT = 14;
  const padB = 32;
  const cw = w - padL - padR;
  const ch = h - padT - padB;
  const prev = opts.prev && opts.prev.length ? opts.prev : null;
  const sum = (set: IColumnLayer[], i: number) => set.reduce((s, l) => s + (l.data[i] || 0), 0);
  const totals = labels.map((_, i) => sum(layers, i));
  const prevTotals = prev ? labels.map((_, i) => sum(prev, i)) : [];
  const max = opts.maxValue ?? Math.max(1, ...totals, ...prevTotals) * 1.12;
  drawGrid(ctx, colors, w, padL, padR, padT, ch, 0, max, opts.yFmt, 4);
  const slotW = cw / n;
  const groups = prev ? 2 : 1;
  const gap = prev ? Math.min(4, slotW * 0.05) : 0;
  const barW = Math.max(2, Math.min(36, (slotW * 0.72 - gap) / groups));
  const centerAt = (i: number) => padL + slotW * (i + 0.5);
  drawXLabels(ctx, colors, labels, centerAt, h - padB + 8);
  const paintStack = (set: IColumnLayer[], i: number, left: number, alpha: number) => {
    let yTop = padT + ch;
    ctx.globalAlpha = alpha;
    set.forEach((layer) => {
      const v = layer.data[i] || 0;
      const segH = (v / max) * ch;
      if (segH <= 0) return;
      ctx.fillStyle = layer.color;
      ctx.fillRect(left, yTop - segH, barW, segH);
      yTop -= segH;
    });
    ctx.globalAlpha = 1;
  };
  labels.forEach((_, i) => {
    const totalW = groups * barW + (groups - 1) * gap;
    const left = centerAt(i) - totalW / 2;
    paintStack(layers, i, left, 1);
    if (prev) paintStack(prev, i, left + barW + gap, 0.45);
  });
  const fmt = (v: number) => (opts.yFmt ? opts.yFmt(v) : Number(v).toLocaleString());
  const extraRows = opts.extraRows ?? [];
  bindHover(canvas, {
    indexAt: (x) => {
      const i = Math.floor((x - padL) / slotW);
      return i >= 0 && i < n ? i : null;
    },
    anchorX: centerAt,
    html: (idx) => {
      const current = layers.map((l) => tipRow(l.color, l.key, fmt(l.data[idx] || 0))).join('');
      const total = layers.length > 1 ? tipRow('transparent', 'Total', fmt(totals[idx])) : '';
      const previous = prev
        ? `<div class="tip-divider"></div>${prev.map((l) => tipRow(l.color, `${l.key} (prev)`, fmt(l.data[idx] || 0), 'opacity:.55;')).join('')}`
        : '';
      return `<div class="tip-title">${esc(labels[idx])}</div>${current}${total}${previous}${extraRowsHtml(extraRows, idx)}`;
    },
  });
}

export function drawStackedBars(canvas: HTMLCanvasElement, categories: string[], groups: IStackGroup[]) {
  const { ctx, w, h } = setupCanvas(canvas);
  const colors = chartColors(canvas);
  ctx.clearRect(0, 0, w, h);
  const padL = 44;
  const padR = 18;
  const padT = 14;
  const padB = 30;
  const cw = w - padL - padR;
  const ch = h - padT - padB;
  const max = 100;
  drawGrid(ctx, colors, w, padL, padR, padT, ch, 0, max, (v) => v + '%', 4);
  const slotW = cw / categories.length;
  const gW = Math.min(60, slotW * 0.36);
  const gap = Math.min(8, slotW * 0.08);
  categories.forEach((lbl, i) => {
    const center = padL + slotW * (i + 0.5);
    const totalW = groups.length * gW + (groups.length - 1) * gap;
    const left = center - totalW / 2;
    groups.forEach((g, gi) => {
      let yTop = padT + ch;
      const bx = left + gi * (gW + gap);
      g.stacks.forEach((s) => {
        const v = s.data[i] || 0;
        const segH = (v / max) * ch;
        ctx.fillStyle = s.color;
        ctx.fillRect(bx, yTop - segH, gW, segH);
        yTop -= segH;
      });
      if (g.label && categories.length === 1) {
        ctx.fillStyle = colors.textMuted;
        ctx.font = `500 10px ${colors.font}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(g.label, bx + gW / 2, h - padB + 8);
      }
    });
    if (categories.length > 1) {
      ctx.fillStyle = colors.text;
      ctx.font = `600 11px ${colors.font}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(lbl, center, h - padB + 8);
    }
  });
}

export function drawSpark(canvas: HTMLCanvasElement, values: number[], color?: string) {
  const { ctx, w, h } = setupCanvas(canvas);
  const colors = chartColors(canvas);
  ctx.clearRect(0, 0, w, h);
  if (!values || values.length === 0) return;
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const stroke = color || colors.text;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  values.forEach((v, i) => {
    const x = w * (values.length === 1 ? 0.5 : i / (values.length - 1));
    const y = h - ((v - min) / (max - min)) * (h - 4) - 2;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
  const lv = values[values.length - 1];
  const ly = h - ((lv - min) / (max - min)) * (h - 4) - 2;
  ctx.fillStyle = stroke;
  ctx.beginPath();
  ctx.arc(Math.min(w, w - 2), ly, 2.4, 0, Math.PI * 2);
  ctx.fill();
}
